import { useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Checkbox, FormControlLabel,
  Button, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  getSecurityHubConfig, listStandards, updateStandards, updateRegions, disableSecurityHub,
  getConfigStatus, enableConfigRecorders,
  startRolesScan, getRolesJob, fixRoles,
} from '../../../services/securityhubApi';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import MultiSelectField from './MultiSelectField';
import { missingEnableCells } from './configStatus';
import ConfigSetupDialog from './ConfigSetupDialog';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading } from '../../../components/portal/PortalStates';
import {
  CK, FONT_BODY, listCardSx, sectionCardSx, sectionHeadingSx, actionRowSx,
  dialogProps, dialogTitleSx,
} from '../portalStyles';

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage standards, linking regions, and Security Hub status"
  />
);

const alertSx = {
  borderRadius: '8px',
  fontFamily: FONT_BODY,
  '& .MuiAlert-message': { fontFamily: FONT_BODY },
};

const helpTextSx = {
  fontFamily: FONT_BODY,
  fontSize: '0.875rem',
  color: CK.body,
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [allStandards, setAllStandards] = useState([]);
  const [standards, setStandards] = useState([]);
  const [regions, setRegions] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [configDialog, setConfigDialog] = useState({ open: false, mode: null, cells: [] });
  const [pendingEnableCells, setPendingEnableCells] = useState([]);
  // Non-blocking banner shown after standards save when AWS Config recorders are missing.
  const [configBanner, setConfigBanner] = useState(null); // null | { cells: [{accountId, region}] }
  const [bannerBusy, setBannerBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, s] = await Promise.all([getSecurityHubConfig(), listStandards()]);
        const cfg = c?.data;
        setConfig(cfg);
        setAllStandards(s?.data || []);
        if (cfg) {
          try { setStandards(JSON.parse(cfg.enabledStandards || '[]')); } catch (_) { setStandards([]); }
          try { setRegions(JSON.parse(cfg.linkingRegions || '[]')); } catch (_) { setRegions([]); }
        }
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveStandards = async () => {
    setBusy(true);
    try {
      await updateStandards(standards);
      setErr(null);
      // Show non-intrusive success confirmation immediately after save.
      setMsg('Standards saved');
      setTimeout(() => setMsg(null), 4000);
      const cs = await getConfigStatus().catch(() => null);
      const cells = missingEnableCells(cs?.data?.recorders || [], regions);
      if (cells.length > 0) {
        // Show non-blocking in-page banner instead of auto-popping a modal.
        setConfigBanner({ cells });
        return; // stay on page so user can read both the success and the warning
      }
      // Brief pause so the "Standards saved" toast is visible before navigating.
      setTimeout(() => navigate('/securityhub/cspm'), 1200);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const enableConfigFromBanner = async () => {
    if (!configBanner) return;
    setBannerBusy(true);
    try {
      await enableConfigRecorders(configBanner.cells, 'minimal');
      setConfigBanner(null);
      setMsg('AWS Config recording enabled — track progress on the AWS Config page.');
      setTimeout(() => setMsg(null), 6000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBannerBusy(false);
    }
  };

  const saveRegions = async () => {
    if (!regions.includes(config.homeRegion)) {
      setErr('Home region cannot be removed');
      return;
    }
    setBusy(true);
    try {
      await updateRegions(regions);
      setErr(null);
      // Regions removed from CSPM linking no longer need recorders (Config is
      // CSPM-only). If a removed region still has an enabled recorder, offer to
      // stop it. Then, if newly-kept linking regions lack recorders, offer enable.
      let oldLinking = [];
      try { oldLinking = JSON.parse(config?.linkingRegions || '[]'); } catch (_) { oldLinking = []; }
      const removed = oldLinking.filter((r) => !regions.includes(r));
      const cs = await getConfigStatus().catch(() => null);
      const recs = cs?.data?.recorders || [];
      const removedActiveCells = recs
        .filter((r) => removed.includes(r.region) && r.status === 'enabled')
        .map(({ accountId, region }) => ({ accountId, region }));
      const enableCells = missingEnableCells(recs, regions);
      if (removedActiveCells.length > 0) {
        setPendingEnableCells(enableCells);
        setConfigDialog({ open: true, mode: 'disable', cells: removedActiveCells });
        return;
      }
      if (enableCells.length > 0) {
        setConfigDialog({ open: true, mode: 'enable', cells: enableCells });
        return;
      }
      navigate('/securityhub/cspm');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const closeConfigDialog = () => {
    if (configDialog.mode === 'disable' && pendingEnableCells.length > 0) {
      const cells = pendingEnableCells;
      setPendingEnableCells([]);
      setConfigDialog({ open: true, mode: 'enable', cells });
      return;
    }
    setConfigDialog({ open: false, mode: null, cells: [] });
    navigate('/securityhub/cspm');
  };

  const doDisable = async () => {
    setBusy(true);
    try {
      await disableSecurityHub();
      setConfirmOpen(false);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner during teardown, then the setup wizard once
      // status transitions to "disabled".
      navigate('/securityhub/cspm');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Security Hub settings…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" sx={alertSx} action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/cspm')}>
              Go to setup
            </Button>
          }>
            Security Hub is not set up yet for this customer. Complete the setup wizard first to manage standards, regions, or disable the service.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>

      {msg && (
        <motion.div variants={sectionVariants}>
          <Alert severity="success" sx={{ ...alertSx, mb: 2 }}>{msg}</Alert>
        </motion.div>
      )}
      {err && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ ...alertSx, mb: 2 }}>{err}</Alert>
        </motion.div>
      )}
      {configBanner && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ ...alertSx, mb: 2 }}
            action={
              <Box sx={actionRowSx}>
                <Button color="inherit" size="small" onClick={() => navigate('/securityhub/config')} disabled={bannerBusy}>
                  Open AWS Config page
                </Button>
                <Button color="inherit" size="small" variant="outlined" onClick={enableConfigFromBanner} disabled={bannerBusy}>
                  Enable AWS Config
                </Button>
                <Button color="inherit" size="small" onClick={() => setConfigBanner(null)} disabled={bannerBusy}>
                  Later
                </Button>
              </Box>
            }
          >
            AWS Config isn&apos;t recording in {configBanner.cells.length} account/region pair(s).
            Enable minimal recording so your standards can fully evaluate.
          </Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: `1px solid ${CK.border}` }}
          >
            <Tab label="Standards" />
            <Tab label="Regions" />
            <Tab label="Permissions" />
            <Tab label="Danger zone" />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Compliance standards</Typography>
              <Typography sx={{ ...helpTextSx, mb: 2 }}>
                Selected standards are enabled uniformly across every enrolled account and region.
              </Typography>
              {allStandards.map((s) => (
                <FormControlLabel
                  key={s.arn}
                  control={
                    <Checkbox
                      checked={standards.includes(s.arn)}
                      onChange={(e) => setStandards((cur) =>
                        e.target.checked ? [...cur, s.arn] : cur.filter((x) => x !== s.arn))}
                    />
                  }
                  label={s.displayName}
                  sx={{ display: 'block' }}
                />
              ))}
              <Button
                variant="contained"
                disabled={busy || standards.length === 0}
                onClick={saveStandards}
                sx={{ mt: 2 }}
              >
                Save standards
              </Button>
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Regions</Typography>
              <Typography sx={{ ...helpTextSx, mb: 2 }}>
                Home region: <b style={{ color: CK.navy }}>{config.homeRegion}</b> (read-only)
              </Typography>
              <Box sx={{ mb: 2 }}>
                <MultiSelectField
                  label="Linking regions"
                  options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
                  value={regions}
                  onChange={setRegions}
                  lockedValues={config.homeRegion ? [config.homeRegion] : []}
                />
              </Box>
              <Button variant="contained" disabled={busy} onClick={saveRegions}>Save regions</Button>
            </Box>
          )}

          {tab === 2 && (
            <Box sx={{ p: 3 }}>
              <RolePermissionsPanel
                startScan={startRolesScan}
                getJob={getRolesJob}
                fix={fixRoles}
                capabilityLabel="Security Hub"
              />
            </Box>
          )}

          {tab === 3 && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Disable Security Hub</Typography>
              <Alert severity="warning" sx={{ ...alertSx, mb: 2 }}>
                Disabling tears down all member enrollments, the finding aggregator, and
                Security Hub in every region. Finding history in Prism is retained;
                standards progress is lost.
              </Alert>
              <Button color="error" variant="contained" onClick={() => setConfirmOpen(true)}>
                Disable Security Hub
              </Button>
            </Box>
          )}
        </Paper>
      </motion.div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Confirm disable</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={helpTextSx}>
            Type the customer ID <b style={{ color: CK.navy }}>{config.customerId}</b> to confirm:
          </Typography>
          <TextField
            fullWidth
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy || confirm !== config.customerId} onClick={doDisable}>
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      <ConfigSetupDialog
        open={configDialog.open}
        mode={configDialog.mode}
        cells={configDialog.cells}
        onClose={closeConfigDialog}
      />
    </motion.div>
  );
}
